import React, { useState, useCallback } from 'react';
import '../../styles/AbaPayRuns.css';
import { FiDownload, FiCheckCircle, FiXCircle, FiRefreshCw, FiFileText } from 'react-icons/fi';
import Cookies from 'js-cookie';
import CommonLoader from '../../../../components/common/CommonLoader';
import { GRAPHQL_HTTP_URL } from '../../../../config/api';

const GQL = GRAPHQL_HTTP_URL;

// ── GraphQL ────────────────────────────────────────────────────────────────

// Fetch ALL ABA pay runs — filter to contractor ones (payrunLocked === false)
const ABA_PAY_RUNS_QUERY = `
  query {
    abaPayRuns {
      id
      payPeriodStart
      payPeriodEnd
      description
      remitterName
      bankAbbreviation
      apcaId
      totalCreditAmountCents
      employeeCount
      status
      generatedAt
      approvedAt
      releasedAt
      payrunLocked
      voidReason
    }
  }
`;

const APPROVE_MUTATION = `
  mutation ApproveAba($id: String!, $comment: String) {
    approveAbaPayRun(id: $id, comment: $comment) {
      id
      status
      approvedAt
    }
  }
`;

const RELEASE_MUTATION = `
  mutation ReleaseAba($id: String!, $comment: String) {
    releaseAbaPayRun(id: $id, comment: $comment) {
      payRun { id status releasedAt }
      abaFileContent
    }
  }
`;

const VOID_MUTATION = `
  mutation VoidAba($id: String!, $reason: String!) {
    voidAbaPayRun(id: $id, reason: $reason) {
      id
      status
      voidReason
    }
  }
`;

// ── Helpers ────────────────────────────────────────────────────────────────

const gql = async (query, variables = {}) => {
    const token = Cookies.get('accessToken');
    const res   = await fetch(GQL, {
        method:  'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors[0].message);
    return json.data;
};

const fmt = (cents) =>
    `$${(Number(cents ?? 0) / 100).toLocaleString('en-AU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const fmtDate = (d) => {
    if (!d) return '—';
    const [y, m, day] = String(d).split('T')[0].split('-');
    return `${day}/${m}/${y}`;
};

const fmtDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const STATUS_META = {
    PENDING_APPROVAL: { label: 'Pending Approval', cls: 'aba-badge--pending'  },
    APPROVED:         { label: 'Approved',          cls: 'aba-badge--approved' },
    RELEASED:         { label: 'Released',           cls: 'aba-badge--released' },
    VOIDED:           { label: 'Voided',             cls: 'aba-badge--voided'  },
    DRAFT:            { label: 'Draft',              cls: 'aba-badge--draft'   },
};

const downloadAba = (content, periodStart, periodEnd) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `contractor_${String(periodStart).split('T')[0]}_${String(periodEnd).split('T')[0]}.aba`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// ── Component ──────────────────────────────────────────────────────────────

export default function ContractorAbaPayRuns() {
    const [runs, setRuns]             = useState(null);
    const [loading, setLoading]       = useState(false);
    const [actionId, setActionId]     = useState(null);
    const [error, setError]           = useState('');
    const [voidModal, setVoidModal]   = useState(null);   // { id, status }
    const [voidReason, setVoidReason] = useState('');

    // ── Load — only contractor ABA runs (payrunLocked === false) ──────────
    const loadRuns = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await gql(ABA_PAY_RUNS_QUERY);
            const all  = data.abaPayRuns ?? [];
            // Active contractor runs have payrunLocked = false (set at creation).
            // Exclude VOIDED rows: after void, payrunLocked stays false, so without
            // this guard voided employee runs would leak into the contractor list.
            // Voided runs of any type are shown in the employee ABA Pay Runs history.
            setRuns(all.filter(r => r.payrunLocked === false && r.status !== 'VOIDED'));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => { loadRuns(); }, [loadRuns]);

    // ── Approve ───────────────────────────────────────────────────────────
    const handleApprove = async (id) => {
        setActionId(id);
        setError('');
        try {
            await gql(APPROVE_MUTATION, { id });
            await loadRuns();
        } catch (e) {
            setError(e.message);
        } finally {
            setActionId(null);
        }
    };

    // ── Release + Download ────────────────────────────────────────────────
    const handleRelease = async (run) => {
        setActionId(run.id);
        setError('');
        try {
            const data = await gql(RELEASE_MUTATION, { id: run.id });
            const { abaFileContent } = data.releaseAbaPayRun;
            downloadAba(abaFileContent, run.payPeriodStart, run.payPeriodEnd);
            await loadRuns();
        } catch (e) {
            setError(e.message);
        } finally {
            setActionId(null);
        }
    };

    // ── Void ──────────────────────────────────────────────────────────────
    const handleVoidSubmit = async () => {
        if (!voidReason.trim()) return;
        const id = voidModal.id;
        setVoidModal(null);
        setActionId(id);
        setError('');
        try {
            await gql(VOID_MUTATION, { id, reason: voidReason.trim() });
            setVoidReason('');
            await loadRuns();
        } catch (e) {
            setError(e.message);
        } finally {
            setActionId(null);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="aba-page">

            {/* Header */}
            <div className="aba-header">
                <div>
                    <h2 className="aba-title">Contractor ABA Pay Runs</h2>
                    <p className="aba-subtitle">
                        Review, approve, and download ABA Direct Entry files for contractor payments.
                        After releasing, upload the <code>.aba</code> file to your bank portal.
                    </p>
                </div>
                <button className="aba-btn aba-btn--refresh" onClick={loadRuns} disabled={loading}>
                    <FiRefreshCw className={loading ? 'aba-spin' : ''} />
                    Refresh
                </button>
            </div>

            {/* How-to banner */}
            <div className="aba-info-banner">
                <strong>How to process payments:</strong>
                &nbsp;1. Approve the pay run &nbsp;→&nbsp;
                2. Click <em>Release &amp; Download</em> to get the <code>.aba</code> file &nbsp;→&nbsp;
                3. Log into your bank's portal and upload the file to initiate the batch payment.
            </div>

            {/* Error banner */}
            {error && <div className="aba-error">{error}</div>}

            {/* Loading */}
            {loading && runs === null && (
                <div className="aba-loader"><CommonLoader /></div>
            )}

            {/* Empty state */}
            {!loading && runs?.length === 0 && (
                <div className="aba-empty">
                    <FiFileText size={40} className="aba-empty-icon" />
                    <p>No contractor ABA pay runs yet.</p>
                    <p className="aba-empty-hint">
                        Go to the Payout tab, select a period and click <em>Generate Payout</em>.
                        An ABA file is created automatically for any contractors paid by bank transfer.
                    </p>
                </div>
            )}

            {/* Table */}
            {runs?.length > 0 && (
                <div className="aba-card">
                    <table className="aba-table">
                        <thead>
                            <tr>
                                <th>Period</th>
                                <th>Description</th>
                                <th>Bank</th>
                                <th>Contractors</th>
                                <th>Total</th>
                                <th>Generated</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map(run => {
                                const meta    = STATUS_META[run.status] ?? STATUS_META.DRAFT;
                                const busy    = actionId === run.id;
                                const isPend  = run.status === 'PENDING_APPROVAL';
                                const isApprv = run.status === 'APPROVED';
                                const isRel   = run.status === 'RELEASED';
                                const isVoid  = run.status === 'VOIDED';

                                return (
                                    <tr key={run.id}>
                                        <td className="aba-period">
                                            {fmtDate(run.payPeriodStart)} – {fmtDate(run.payPeriodEnd)}
                                        </td>
                                        <td>{run.description || '—'}</td>
                                        <td>
                                            <span className="aba-bank">{run.bankAbbreviation || '—'}</span>
                                        </td>
                                        <td className="aba-center">{run.employeeCount}</td>
                                        <td className="aba-amount">{fmt(run.totalCreditAmountCents)}</td>
                                        <td className="aba-dt">{fmtDateTime(run.generatedAt)}</td>
                                        <td>
                                            <span className={`aba-badge ${meta.cls}`}>{meta.label}</span>
                                        </td>
                                        <td>
                                            <div className="aba-actions">
                                                {isPend && (
                                                    <button
                                                        className="aba-btn aba-btn--approve"
                                                        onClick={() => handleApprove(run.id)}
                                                        disabled={busy}
                                                    >
                                                        <FiCheckCircle />
                                                        {busy ? '…' : 'Approve'}
                                                    </button>
                                                )}
                                                {isApprv && (
                                                    <button
                                                        className="aba-btn aba-btn--release"
                                                        onClick={() => handleRelease(run)}
                                                        disabled={busy}
                                                    >
                                                        <FiDownload />
                                                        {busy ? 'Downloading…' : 'Release & Download'}
                                                    </button>
                                                )}
                                                {isRel && (
                                                    <button
                                                        className="aba-btn aba-btn--release"
                                                        onClick={() => handleRelease(run)}
                                                        disabled={busy}
                                                    >
                                                        <FiDownload />
                                                        {busy ? 'Downloading…' : 'Download again'}
                                                    </button>
                                                )}
                                                {!isVoid && (
                                                    <button
                                                        className="aba-btn aba-btn--void"
                                                        onClick={() => { setVoidModal({ id: run.id, status: run.status }); setVoidReason(''); }}
                                                        disabled={busy}
                                                    >
                                                        <FiXCircle />
                                                        Void
                                                    </button>
                                                )}
                                                {run.status === 'RELEASED' && (
                                                    <span className="aba-released-tag">File sent</span>
                                                )}
                                                {run.status === 'VOIDED' && run.voidReason && (
                                                    <span className="aba-void-reason" title={run.voidReason}>
                                                        {run.voidReason.slice(0, 30)}{run.voidReason.length > 30 ? '…' : ''}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Void modal */}
            {voidModal && (
                <div className="aba-modal-overlay" onClick={() => setVoidModal(null)}>
                    <div className="aba-modal" onClick={e => e.stopPropagation()}>
                        <h3 className="aba-modal-title">Void Contractor ABA Pay Run</h3>
                        {voidModal?.status === 'RELEASED' && (
                            <p className="aba-modal-desc" style={{ color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px' }}>
                                This ABA file has already been released to the bank. Voiding here does <strong>not</strong> reverse the bank payment — contact your bank separately to reverse the transfer.
                            </p>
                        )}
                        <p className="aba-modal-desc">
                            Provide a reason for voiding this pay run:
                        </p>
                        <textarea
                            className="aba-modal-textarea"
                            placeholder="e.g. Incorrect bank details, regenerating..."
                            value={voidReason}
                            onChange={e => setVoidReason(e.target.value)}
                            rows={3}
                            autoFocus
                        />
                        <div className="aba-modal-footer">
                            <button className="aba-btn aba-btn--cancel" onClick={() => setVoidModal(null)}>
                                Cancel
                            </button>
                            <button
                                className="aba-btn aba-btn--void"
                                onClick={handleVoidSubmit}
                                disabled={!voidReason.trim()}
                            >
                                Confirm Void
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
